import JSZip from "jszip";
import { z } from "zod";
import type { Asset, AudioCategory, AudioFolder, WorkspaceState } from "../types";
import { readOpfsFile } from "./workspace-client";
import { GLOBAL_MEDIA_CAMPAIGN_ID, defaultAudioMixer, defaultProjectionAwareness } from "./defaults";
import { AIDM_NARRATOR_PROFILE_VERSION, DEFAULT_AIDM_COST_MODE, DEFAULT_AIDM_LANGUAGE, DEFAULT_AIDM_MODEL, DEFAULT_AIDM_VOICE, normalizeAidmVoicePreference } from "./aidm";
import { normalizeTokenGridSnapping } from "./token-grid";

const normalizeAudioFolderList = (folders: unknown): AudioFolder[] => {
  if (!Array.isArray(folders)) return [];
  return folders.filter((folder: unknown): folder is AudioFolder => {
    if (!folder || typeof folder !== "object") return false;
    const candidate = folder as Partial<AudioFolder> & { category?: AudioCategory };
    return typeof candidate.id === "string" && typeof candidate.name === "string" &&
      (candidate.category === "ambience" || candidate.category === "sfx" || candidate.category === "music");
  });
};

const ManifestSchema = z.object({
  format: z.union([z.literal("tbltop"), z.literal("jftt")]),
  version: z.union([z.literal(1), z.literal(2)]),
  scope: z.enum(["campaign", "workspace"]),
  createdAt: z.number(),
  campaignId: z.string().optional(),
  assetHashes: z.record(z.string(), z.string()),
});

export type BackupManifest = z.infer<typeof ManifestSchema>;
export type BackupProgress = { phase: "collecting" | "packing" | "saving" | "complete"; percent: number; detail: string };
export type BackupDownloadTarget = { filename: string };

const report = (onProgress: ((progress: BackupProgress) => void) | undefined, progress: BackupProgress) => onProgress?.(progress);

function campaignReferencedAssetIds(state: WorkspaceState, campaignId: string, scenes: WorkspaceState["scenes"]) {
  const referenced = new Set<string>();
  for (const scene of scenes) {
    if (scene.assetId) referenced.add(scene.assetId);
    if (scene.story?.imageAssetId) referenced.add(scene.story.imageAssetId);
    for (const token of scene.tokens) if (token.assetId) referenced.add(token.assetId);
    for (const region of scene.materialRegions ?? []) if (region.soundAssetId) referenced.add(region.soundAssetId);
    for (const binding of Object.values(scene.environmentSounds ?? {})) if (binding?.assetId) referenced.add(binding.assetId);
  }
  const campaign = state.campaigns.find((candidate) => candidate.id === campaignId);
  for (const narration of campaign?.aidm?.narration ?? []) if (narration.audioAssetId) referenced.add(narration.audioAssetId);
  for (const playlist of state.settings.audioPlaylists ?? []) {
    if (playlist.campaignId === campaignId) for (const assetId of playlist.assetIds) referenced.add(assetId);
  }
  for (const sequence of state.settings.cueSequences ?? []) {
    if (sequence.campaignId === campaignId) for (const step of sequence.steps) if (step.audioAssetId) referenced.add(step.audioAssetId);
  }
  return referenced;
}

export function backupAssetsForCampaign(state: WorkspaceState, campaignId?: string): Asset[] {
  if (!campaignId) return state.assets;
  const scenes = state.scenes.filter((scene) => scene.campaignId === campaignId);
  const referenced = campaignReferencedAssetIds(state, campaignId, scenes);
  return state.assets.filter((asset) => asset.campaignId === campaignId || (asset.campaignId === GLOBAL_MEDIA_CAMPAIGN_ID && referenced.has(asset.id)));
}

export async function createBackup(state: WorkspaceState, database: Uint8Array | undefined, campaignId?: string, onProgress?: (progress: BackupProgress) => void): Promise<Blob> {
  const zip = new JSZip();
  report(onProgress, { phase: "collecting", percent: 2, detail: "Collecting campaign data…" });
  const campaignIds = campaignId ? new Set([campaignId]) : new Set(state.campaigns.map((campaign) => campaign.id));
  const scenes = state.scenes.filter((scene) => campaignIds.has(scene.campaignId));
  const assets = backupAssetsForCampaign(state, campaignId);
  const mixer = state.settings.audioMixer ?? defaultAudioMixer();
  const exportedTrackIds = new Set(assets.filter((asset) => asset.kind === "audio").map((asset) => asset.id));
  const audioMixer = { ...mixer, tracks: Object.fromEntries(Object.entries(mixer.tracks).filter(([assetId]) => exportedTrackIds.has(assetId))) };
  const exported: WorkspaceState = {
    ...state,
    campaigns: state.campaigns.filter((campaign) => campaignIds.has(campaign.id)),
    heroes: state.heroes.filter((hero) => campaignIds.has(hero.campaignId)),
    scenes,
    assets,
    presentation: campaignId ? { ...state.presentation, campaignId, activeSceneId: scenes[0]?.id, overrideSceneId: undefined, stagedSceneId: undefined } : state.presentation,
    settings: { storagePersistent: state.settings.storagePersistent, activeCampaignId: campaignId ?? state.settings.activeCampaignId, audioMixer, dmTableEdge: state.settings.dmTableEdge, coverColor: state.settings.coverColor, sceneFolders: (state.settings.sceneFolders ?? []).filter((folder) => campaignIds.has(folder.campaignId)), tokenPresets: (state.settings.tokenPresets ?? []).filter((preset) => campaignIds.has(preset.campaignId)), audioFolders: normalizeAudioFolderList(state.settings.audioFolders), bundledMediaVersion: state.settings.bundledMediaVersion, suppressedBundledMediaIds: state.settings.suppressedBundledMediaIds ?? [], audioPlaylists: (state.settings.audioPlaylists ?? []).filter((playlist) => campaignIds.has(playlist.campaignId)), cueSequences: (state.settings.cueSequences ?? []).filter((sequence) => campaignIds.has(sequence.campaignId)), aidmModel: state.settings.aidmModel ?? DEFAULT_AIDM_MODEL, aidmLanguage: state.settings.aidmLanguage ?? DEFAULT_AIDM_LANGUAGE, aidmCostMode: state.settings.aidmCostMode ?? DEFAULT_AIDM_COST_MODE, aidmVoice: state.settings.aidmVoice ?? DEFAULT_AIDM_VOICE, aidmAutoNarrate: state.settings.aidmAutoNarrate === true, aidmNarratorProfileVersion: AIDM_NARRATOR_PROFILE_VERSION, projectionAwareness: state.settings.projectionAwareness ?? defaultProjectionAwareness(), tokenGridSnapping: normalizeTokenGridSnapping(state.settings.tokenGridSnapping) },
  };
  const hashes: Record<string, string> = {};
  for (let index = 0; index < assets.length; index += 1) {
    const asset = assets[index];
    report(onProgress, { phase: "collecting", percent: 5 + Math.round((index / Math.max(1, assets.length)) * 50), detail: `Adding ${asset.name} (${index + 1} of ${assets.length})…` });
    const file = await readOpfsFile(asset.opfsPath);
    if (!file) throw new Error(`The local file for ${asset.name} is missing.`);
    zip.file(`assets/${asset.hash}`, file);
    hashes[asset.id] = asset.hash;
  }
  zip.file("manifest.json", JSON.stringify({
    format: "tbltop", version: 2, scope: campaignId ? "campaign" : "workspace",
    createdAt: Date.now(), campaignId, assetHashes: hashes,
  }, null, 2));
  zip.file("workspace.json", JSON.stringify(exported));
  if (database && !campaignId) zip.file("workspace.sqlite3", database);
  report(onProgress, { phase: "packing", percent: 56, detail: "Packing the portable archive…" });
  const blob = await zip.generateAsync({ type: "blob", compression: "DEFLATE", compressionOptions: { level: 6 }, mimeType: "application/vnd.tbltop+zip" }, (metadata) => {
    report(onProgress, { phase: "packing", percent: 56 + Math.round(metadata.percent * .41), detail: "Packing the portable archive…" });
  });
  report(onProgress, { phase: "saving", percent: 98, detail: "Archive ready. Saving download…" });
  return blob;
}

/** A compact, developer-friendly export of the global audio library only. */
export async function createMediaLibraryBackup(state: WorkspaceState): Promise<Blob> {
  const zip = new JSZip();
  const assets = state.assets.filter((asset) => asset.campaignId === GLOBAL_MEDIA_CAMPAIGN_ID && asset.kind === "audio");
  const folders = normalizeAudioFolderList(state.settings.audioFolders);
  const files = new Set<string>();
  const manifestAssets = [];
  for (const asset of assets) {
    const file = await readOpfsFile(asset.opfsPath);
    if (!file) throw new Error(`The local file for ${asset.name} is missing.`);
    const extension = asset.opfsPath.split(".").pop()?.toLowerCase().replace(/[^a-z0-9]/g, "") || asset.name.split(".").pop()?.toLowerCase().replace(/[^a-z0-9]/g, "") || "bin";
    const bundledFile = `${asset.hash}.${extension}`;
    if (!files.has(bundledFile)) { zip.file(`files/${bundledFile}`, file, { compression: "STORE" }); files.add(bundledFile); }
    manifestAssets.push({ id: asset.id, name: asset.name, category: asset.audioCategory ?? "ambience", folderId: asset.audioFolderId, mime: asset.mime, size: asset.size, hash: asset.hash, file: bundledFile, duration: asset.duration, createdAt: asset.createdAt });
  }
  zip.file("media-manifest.json", JSON.stringify({ format: "tbltop-default-media", version: 1, libraryVersion: `export-${Date.now()}`, folders, assets: manifestAssets }, null, 2));
  return zip.generateAsync({ type: "blob", compression: "STORE", mimeType: "application/vnd.tbltop-media+zip" });
}

export async function readBackup(file: File, onProgress?: (percent: number, detail: string) => void): Promise<{ state: WorkspaceState; files: Map<string, Blob>; manifest: BackupManifest }> {
  onProgress?.(2, "Opening the TBLTOP archive…");
  const zip = await JSZip.loadAsync(file, { checkCRC32: true });
  const entries = Object.keys(zip.files);
  if (entries.length > 10_000 || entries.some((path) => path.includes("..") || path.startsWith("/") || path.includes("\\"))) throw new Error("The backup contains unsafe archive paths.");
  const manifestEntry = zip.file("manifest.json"); const workspaceEntry = zip.file("workspace.json");
  if (!manifestEntry || !workspaceEntry) throw new Error("This is not a complete TBLTOP backup.");
  const manifest = ManifestSchema.parse(JSON.parse(await manifestEntry.async("text")));
  const rawState = JSON.parse(await workspaceEntry.async("text")) as WorkspaceState;
  if (![1, 2].includes(rawState.schemaVersion) || !Array.isArray(rawState.campaigns) || !Array.isArray(rawState.scenes) || !Array.isArray(rawState.assets)) throw new Error("Unsupported or malformed TBLTOP workspace data.");
  const state: WorkspaceState = { ...rawState, schemaVersion: 2, heroes: Array.isArray(rawState.heroes) ? rawState.heroes : [], settings: { ...rawState.settings, audioMixer: rawState.settings?.audioMixer ?? defaultAudioMixer(), audioFolders: normalizeAudioFolderList(rawState.settings?.audioFolders), cueSequences: rawState.settings?.cueSequences ?? [], aidmModel: rawState.settings?.aidmModel ?? DEFAULT_AIDM_MODEL, aidmLanguage: rawState.settings?.aidmLanguage ?? DEFAULT_AIDM_LANGUAGE, aidmCostMode: rawState.settings?.aidmCostMode ?? DEFAULT_AIDM_COST_MODE, aidmVoice: normalizeAidmVoicePreference(rawState.settings?.aidmVoice, rawState.settings?.aidmNarratorProfileVersion), aidmAutoNarrate: rawState.settings?.aidmAutoNarrate === true, aidmNarratorProfileVersion: AIDM_NARRATOR_PROFILE_VERSION, projectionAwareness: rawState.settings?.projectionAwareness ?? defaultProjectionAwareness(), tokenGridSnapping: normalizeTokenGridSnapping(rawState.settings?.tokenGridSnapping) } };
  const files = new Map<string, Blob>();
  let expanded = 0;
  const hashes = [...new Set(Object.values(manifest.assetHashes))];
  for (let index = 0; index < hashes.length; index += 1) {
    const hash = hashes[index];
    onProgress?.(10 + Math.round((index / Math.max(1, hashes.length)) * 80), `Reading asset ${index + 1} of ${hashes.length}…`);
    const entry = zip.file(`assets/${hash}`);
    if (!entry) throw new Error(`Backup asset ${hash.slice(0, 12)} is missing.`);
    const blob = await entry.async("blob"); expanded += blob.size;
    if (expanded > 2 * 1024 * 1024 * 1024) throw new Error("The expanded backup exceeds the 2 GiB safety limit.");
    files.set(hash, blob);
  }
  onProgress?.(92, "Validating campaign data…");
  return { state, files, manifest };
}

/** Keep exports on the standard browser download path.
 *
 * Chromium's File System Access picker can remain hidden behind an installed
 * app or projector window and leave the export apparently stuck. A normal
 * browser download works consistently across Chromium, Safari, and Firefox.
 */
export async function prepareBackupDownload(filename: string): Promise<BackupDownloadTarget> {
  return { filename };
}

export async function downloadBackup(blob: Blob, target: string | BackupDownloadTarget) {
  const resolved = typeof target === "string" ? { filename: target } : target;
  const url = URL.createObjectURL(blob); const link = document.createElement("a");
  link.href = url; link.download = resolved.filename; link.style.display = "none"; document.body.append(link); link.click(); link.remove(); setTimeout(() => URL.revokeObjectURL(url), 5_000);
}

const replaceCampaignItems = <T extends { campaignId: string }>(current: T[] | undefined, incoming: T[] | undefined, campaignId: string) => [
  ...(current ?? []).filter((item) => item.campaignId !== campaignId),
  ...(incoming ?? []).filter((item) => item.campaignId === campaignId),
];

/** Merge a portable campaign without deleting unrelated campaigns or destination-only settings. */
export function mergeCampaignBackup(current: WorkspaceState, incoming: WorkspaceState, campaignId?: string): WorkspaceState {
  const importedCampaignId = campaignId ?? incoming.settings.activeCampaignId ?? incoming.campaigns[0]?.id;
  const importedCampaign = incoming.campaigns.find((candidate) => candidate.id === importedCampaignId);
  if (!importedCampaignId || !importedCampaign) throw new Error("The campaign backup does not contain a campaign.");
  const importedCampaignAssets = incoming.assets.filter((asset) => asset.campaignId === importedCampaignId);
  const importedGlobalAssets = incoming.assets.filter((asset) => asset.campaignId === GLOBAL_MEDIA_CAMPAIGN_ID);
  const retainedAssets = current.assets.filter((asset) => asset.campaignId !== importedCampaignId);
  for (const asset of importedCampaignAssets) {
    const collision = retainedAssets.find((candidate) => candidate.id === asset.id);
    if (collision) throw new Error(`The asset ID for ${asset.name} is already used by another campaign.`);
  }
  for (const asset of importedGlobalAssets) {
    const collision = retainedAssets.find((candidate) => candidate.id === asset.id);
    if (collision && collision.hash !== asset.hash) throw new Error(`The shared audio ID for ${asset.name} refers to different files.`);
    if (!collision) retainedAssets.push(asset);
  }
  const importedAssetIds = new Set(incoming.assets.map((asset) => asset.id));
  const currentMixer = current.settings.audioMixer ?? defaultAudioMixer();
  const incomingMixer = incoming.settings.audioMixer ?? defaultAudioMixer();
  const importedTracks = Object.fromEntries(Object.entries(incomingMixer.tracks).filter(([assetId]) => importedAssetIds.has(assetId)));
  const audioFolders = [...normalizeAudioFolderList(current.settings.audioFolders)];
  for (const folder of normalizeAudioFolderList(incoming.settings.audioFolders)) if (!audioFolders.some((candidate) => candidate.id === folder.id)) audioFolders.push(folder);
  return {
    ...current,
    campaigns: [...current.campaigns.filter((campaign) => campaign.id !== importedCampaignId), importedCampaign],
    heroes: replaceCampaignItems(current.heroes, incoming.heroes, importedCampaignId),
    scenes: replaceCampaignItems(current.scenes, incoming.scenes, importedCampaignId),
    assets: [...retainedAssets, ...importedCampaignAssets],
    settings: {
      ...current.settings,
      activeCampaignId: importedCampaignId,
      sceneFolders: replaceCampaignItems(current.settings.sceneFolders, incoming.settings.sceneFolders, importedCampaignId),
      tokenPresets: replaceCampaignItems(current.settings.tokenPresets, incoming.settings.tokenPresets, importedCampaignId),
      audioPlaylists: replaceCampaignItems(current.settings.audioPlaylists, incoming.settings.audioPlaylists, importedCampaignId),
      cueSequences: replaceCampaignItems(current.settings.cueSequences, incoming.settings.cueSequences, importedCampaignId),
      audioFolders,
      audioMixer: { ...currentMixer, tracks: { ...currentMixer.tracks, ...importedTracks } },
    },
    presentation: current.presentation,
  } satisfies WorkspaceState;
}

export async function downloadMediaLibrary(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob); const link = document.createElement("a");
  link.href = url; link.download = filename; link.click(); setTimeout(() => URL.revokeObjectURL(url), 5_000);
}
